// pages/SalesforceIntegration.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  RefreshCw,
  AlertCircle,
  Settings,
  Database,
  TestTube,
  FileText,
  Download,
  Upload,
  Trash2,
  Users,
  Building2,
  Target,
  Contact,
  Package,
  HardDrive,
  FolderKanban,
  CheckSquare,
  Calendar,
  ListTodo,
  MessageSquare,
  Phone,
  Wifi,
  Layers,
  FileArchive,
  Paperclip,
  Rss,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

export default function SalesforceIntegration() {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [integration, setIntegration] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [testResults, setTestResults] = useState(null);
  const [syncResults, setSyncResults] = useState({});
  const [sampleLead, setSampleLead] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const [syncingStates, setSyncingStates] = useState({});

  const syncEntities = [
    {
      id: "users",
      name: "Users",
      icon: Users,
      description: "Sync user profiles and permissions",
      color: "blue",
      syncMethod: "syncSalesforceUsers",
    },
    {
      id: "leads",
      name: "Leads",
      icon: Target,
      description: "Sync lead records",
      color: "purple",
      syncMethod: "syncSalesforceLeads",
    },
    {
      id: "accounts",
      name: "Accounts",
      icon: Building2,
      description: "Sync account/company records",
      color: "green",
      syncMethod: "syncSalesforceAccounts",
    },
    {
      id: "contacts",
      name: "Contacts",
      icon: Contact,
      description: "Sync contact records",
      color: "cyan",
      syncMethod: "syncSalesforceContacts",
    },
    {
      id: "opportunities",
      name: "Opportunities",
      icon: Target,
      description: "Sync sales opportunities",
      color: "yellow",
      syncMethod: "syncSalesforceOpportunities",
    },
    {
      id: "products",
      name: "Products",
      icon: Package,
      description: "Sync product catalog",
      color: "orange",
      syncMethod: "syncSalesforceProducts",
    },
    {
      id: "assets",
      name: "Assets",
      icon: HardDrive,
      description: "Sync asset records",
      color: "red",
      syncMethod: "syncSalesforceAssets",
    },
    {
      id: "projectTemplates",
      name: "Project Templates",
      icon: FolderKanban,
      description: "Sync project templates",
      color: "indigo",
      syncMethod: "syncSalesforceProjectTemplates",
    },
    {
      id: "taskTemplates",
      name: "Task Templates",
      icon: CheckSquare,
      description: "Sync task templates",
      color: "pink",
      syncMethod: "syncSalesforceTaskTemplates",
    },
    {
      id: "projects",
      name: "Projects",
      icon: Layers,
      description: "Sync active projects",
      color: "teal",
      syncMethod: "syncSalesforceProjects",
    },
    {
      id: "projectTasks",
      name: "Project Tasks",
      icon: ListTodo,
      description: "Sync project tasks",
      color: "violet",
      syncMethod: "syncSalesforceProjectTasks",
    },
    {
      id: "visits",
      name: "Visits",
      icon: Calendar,
      description: "Sync visit records",
      color: "lime",
      syncMethod: "syncSalesforceVisits",
    },
    {
      id: "broadbandInfo",
      name: "Broadband Information",
      icon: Wifi,
      description: "Sync broadband data",
      color: "sky",
      syncMethod: "syncSalesforceBroadbandInformation",
    },
    {
      id: "lineInfo",
      name: "Line Information",
      icon: Phone,
      description: "Sync line/telecom data",
      color: "emerald",
      syncMethod: "syncSalesforceLineInformation",
    },
    {
      id: "portsOrders",
      name: "Ports Orders",
      icon: Database,
      description: "Sync port order records",
      color: "amber",
      syncMethod: "syncSalesforcePortsOrders",
    },
    {
      id: "events",
      name: "Events",
      icon: Calendar,
      description: "Sync calendar events",
      color: "rose",
      syncMethod: "syncSalesforceEvents",
    },
    {
      id: "tasks",
      name: "Tasks",
      icon: CheckSquare,
      description: "Sync task activities",
      color: "fuchsia",
      syncMethod: "syncSalesforceTasks",
    },
    {
      id: "cases",
      name: "Cases",
      icon: MessageSquare,
      description: "Sync support cases",
      color: "blue",
      syncMethod: "syncSalesforceCases",
    },
    {
      id: "files",
      name: "Files",
      icon: FileArchive,
      description: "Sync file attachments",
      color: "slate",
      syncMethod: "syncSalesforceFiles",
    },
    {
      id: "attachments",
      name: "Attachments",
      icon: Paperclip,
      description: "Sync document attachments",
      color: "zinc",
      syncMethod: "syncSalesforceAttachments",
    },
    {
      id: "feedAll",
      name: "All Feeds",
      icon: Rss,
      description: "Sync all feed items (posts, comments)",
      color: "purple",
      syncMethod: "syncSalesforceFeedItems",
    },
    {
      id: "caseFeed",
      name: "Case Feeds",
      icon: MessageSquare,
      description: "Sync Case feed items only",
      color: "blue",
      syncMethod: "syncSalesforceCaseFeed",
    },
    {
      id: "accountFeed",
      name: "Account Feeds",
      icon: Building2,
      description: "Sync Account feed items only",
      color: "green",
      syncMethod: "syncSalesforceAccountFeed",
    },
    {
      id: "opportunityFeed",
      name: "Opportunity Feeds",
      icon: Target,
      description: "Sync Opportunity feed items only",
      color: "yellow",
      syncMethod: "syncSalesforceOpportunityFeed",
    },
    {
      id: "leadFeed",
      name: "Lead Feeds",
      icon: Target,
      description: "Sync Lead feed items only",
      color: "purple",
      syncMethod: "syncSalesforceLeadFeed",
    },
    {
      id: "projectFeed",
      name: "Project Feeds",
      icon: Layers,
      description: "Sync Project feed items only",
      color: "teal",
      syncMethod: "syncSalesforceProjectFeed",
    },
    {
      id: "contactFeed",
      name: "Contact Feeds",
      icon: Contact,
      description: "Sync Contact feed items only",
      color: "cyan",
      syncMethod: "syncSalesforceContactFeed",
    },
    {
      id: "contactNotes",
      name: "Content Notes",
      icon: Contact,
      description: "Sync Contact feed items only",
      color: "cyan",
      syncMethod: "syncSalesforceContentNotes",
    },
    {
      id: "emailTemplates",
      name: "Email Templates",
      icon: Contact,
      description: "Sync Contact feed items only",
      color: "cyan",
      syncMethod: "syncSalesforceEmailTemplates",
    },
  ];

  const colorClasses = {
    blue: "bg-blue-600 hover:bg-blue-700",
    purple: "bg-purple-600 hover:bg-purple-700",
    green: "bg-green-600 hover:bg-green-700",
    cyan: "bg-cyan-600 hover:bg-cyan-700",
    yellow: "bg-yellow-600 hover:bg-yellow-700",
    orange: "bg-orange-600 hover:bg-orange-700",
    red: "bg-red-600 hover:bg-red-700",
    indigo: "bg-indigo-600 hover:bg-indigo-700",
    pink: "bg-pink-600 hover:bg-pink-700",
    teal: "bg-teal-600 hover:bg-teal-700",
    violet: "bg-violet-600 hover:bg-violet-700",
    lime: "bg-lime-600 hover:bg-lime-700",
    sky: "bg-sky-600 hover:bg-sky-700",
    emerald: "bg-emerald-600 hover:bg-emerald-700",
    amber: "bg-amber-600 hover:bg-amber-700",
    rose: "bg-rose-600 hover:bg-rose-700",
    fuchsia: "bg-fuchsia-600 hover:bg-fuchsia-700",
    slate: "bg-slate-600 hover:bg-slate-700",
    zinc: "bg-zinc-600 hover:bg-zinc-700",
  };

  useEffect(() => {
    fetchIntegrationStatus();
  }, []);

  const fetchIntegrationStatus = async () => {
    try {
      setLoading(true);
      const response = await apiService.getSalesforceStatus();
      setIntegration(response.integration);
    } catch (error) {
      console.error("Error fetching Salesforce status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await apiService.getSalesforceAuthUrl();
      const width = 600,
        height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      const authWindow = window.open(
        response.authUrl,
        "Salesforce Authorization",
        `width=${width},height=${height},left=${left},top=${top}`,
      );
      const checkWindow = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkWindow);
          fetchIntegrationStatus();
        }
      }, 500);
    } catch (error) {
      console.error("Error connecting Salesforce:", error);
      alert("Failed to connect Salesforce. Please try again.");
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to disconnect Salesforce?"))
      return;
    try {
      await apiService.disconnectSalesforce();
      setIntegration(null);
      alert("Salesforce disconnected successfully");
    } catch (error) {
      console.error("Error disconnecting Salesforce:", error);
      alert("Failed to disconnect Salesforce");
    }
  };

  const handleTestConnection = async () => {
    try {
      setIsTesting(true);
      await apiService.testSalesforceConnection();
      alert("Connection test successful!");
      fetchIntegrationStatus();
    } catch (error) {
      console.error("Error testing connection:", error);
      alert("Connection test failed: " + error.message);
    } finally {
      setIsTesting(false);
    }
  };

  const handleGetSampleLead = async () => {
    try {
      setIsTesting(true);
      const response = await apiService.getSalesforceSampleLead();
      setSampleLead(response.data);
      setActiveTab("test");
    } catch (error) {
      console.error("Error getting sample lead:", error);
      alert("Failed to get sample lead: " + error.message);
    } finally {
      setIsTesting(false);
    }
  };

  const handleAnalyzeLeads = async () => {
    try {
      setIsTesting(true);
      const response = await apiService.analyzeSalesforceLeads(10);
      setAnalysis(response.data);
      setActiveTab("test");
    } catch (error) {
      console.error("Error analyzing leads:", error);
      alert("Failed to analyze leads: " + error.message);
    } finally {
      setIsTesting(false);
    }
  };

  const handleSync = async (entityId, syncMethod) => {
    const entity = syncEntities.find((e) => e.id === entityId);
    if (
      !window.confirm(
        `This will sync all ${entity.name} from Salesforce. This may take a while. Continue?`,
      )
    )
      return;

    try {
      setSyncingStates((prev) => ({ ...prev, [entityId]: true }));
      const options = {
        duplicateStrategy: "update",
        dedupeFields: ["salesforceId"],
      };
      if (entityId === "files") {
        options.limit = 200000;
        options.downloadContent = true;
      }
      if (entityId === "feedAll") {
        options.limit = 5000000;
        options.parentTypes = ["Case", "Account", "Opportunity", "Contact"];
      }

      if (typeof apiService[syncMethod] !== "function")
        throw new Error(`Sync method ${syncMethod} not found in apiService`);
      const response = await apiService[syncMethod](options);
      setSyncResults((prev) => ({
        ...prev,
        [entityId]: response.data || response,
      }));
      setActiveTab("test");
      alert(`${entity.name} sync started successfully!`);
    } catch (error) {
      console.error(`Error syncing ${entity.name}:`, error);
      alert(
        `Failed to sync ${entity.name}: ${error.response?.data?.message || error.message}`,
      );
    } finally {
      setSyncingStates((prev) => ({ ...prev, [entityId]: false }));
    }
  };

  const handleSyncAll = async () => {
    if (
      !window.confirm(
        "This will sync ALL entities from Salesforce. This will take a significant amount of time. Continue?",
      )
    )
      return;
    let successCount = 0,
      failCount = 0;
    const entitiesToSync = syncEntities.filter(
      (e) =>
        ![
          "caseFeed",
          "accountFeed",
          "opportunityFeed",
          "leadFeed",
          "projectFeed",
          "contactFeed",
        ].includes(e.id),
    );

    for (const entity of entitiesToSync) {
      try {
        await handleSync(entity.id, entity.syncMethod);
        successCount++;
        await new Promise((resolve) => setTimeout(resolve, 2000));
      } catch (error) {
        console.error(`Error in bulk sync for ${entity.name}:`, error);
        failCount++;
        if (
          !window.confirm(
            `Sync failed for ${entity.name}. Continue with remaining entities?`,
          )
        )
          break;
      }
    }
    alert(
      `Bulk sync process completed!\nSuccessful: ${successCount}\nFailed: ${failCount}`,
    );
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12]" : "bg-[#F4F5F7]"}`}
      >
        <RefreshCw
          className={`w-8 h-8 animate-spin ${isDarkMode ? "text-[#7F56D9]" : "text-purple-600"}`}
        />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 p-6 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/settings")}
              className={`transition-colors ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-900"}`}
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                <img
                  src="/assets/salesforce.png"
                  alt="Salesforce"
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div>
                <h1 className="text-2xl font-semibold">
                  Salesforce Integration
                </h1>
                <p
                  className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {integration ? integration.organizationName : "Not connected"}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {integration ? (
              <>
                <button
                  onClick={handleTestConnection}
                  disabled={isTesting}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 ${isDarkMode ? "bg-[#1a1d23] hover:bg-[#22262F] text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-700"}`}
                >
                  <RefreshCw
                    className={`w-4 h-4 ${isTesting ? "animate-spin" : ""}`}
                  />
                  Test Connection
                </button>
                <button
                  onClick={handleDisconnect}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Disconnect
                </button>
              </>
            ) : (
              <button
                onClick={handleConnect}
                className="px-6 py-2 bg-[#7F56D9] hover:bg-[#6B46C1] text-white rounded-lg transition-colors"
              >
                Connect Salesforce
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div
          className={`border-b mb-6 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <nav className="flex space-x-8">
            {[
              { id: "overview", label: "Overview" },
              { id: "sync", label: "Sync Entities" },
              { id: "test", label: "Testing & Results" },
              { id: "settings", label: "Settings" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-1 py-4 text-sm font-medium transition-colors ${activeTab === tab.id ? (isDarkMode ? "text-white border-b-2 border-[#7F56D9]" : "text-gray-900 border-b-2 border-purple-600") : isDarkMode ? "text-gray-400 hover:text-gray-300" : "text-gray-500 hover:text-gray-700"}`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <>
              {integration ? (
                <>
                  <div
                    className={`border rounded-lg p-6 transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
                  >
                    <h3
                      className={`text-lg font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      Connection Status
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div
                          className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Organization
                        </div>
                        <div
                          className={
                            isDarkMode ? "text-white" : "text-gray-900"
                          }
                        >
                          {integration.organizationName}
                        </div>
                      </div>
                      <div>
                        <div
                          className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Status
                        </div>
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-green-500">Connected</span>
                        </div>
                      </div>
                      <div>
                        <div
                          className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Last Sync
                        </div>
                        <div
                          className={
                            isDarkMode ? "text-white" : "text-gray-900"
                          }
                        >
                          {integration.lastSyncAt
                            ? new Date(integration.lastSyncAt).toLocaleString()
                            : "Never"}
                        </div>
                      </div>
                      <div>
                        <div
                          className={`text-sm mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Sync Status
                        </div>
                        <div
                          className={
                            isDarkMode ? "text-white" : "text-gray-900"
                          }
                        >
                          {integration.lastSyncStatus || "No syncs yet"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {integration.syncStats && (
                    <div
                      className={`border rounded-lg p-6 transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
                    >
                      <h3
                        className={`text-lg font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        Sync Statistics
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div
                          className={`text-center p-4 rounded-lg ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                        >
                          <div className="text-2xl font-bold text-[#7F56D9]">
                            {integration.syncStats.totalLeadsSynced || 0}
                          </div>
                          <div
                            className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Leads
                          </div>
                        </div>
                        <div
                          className={`text-center p-4 rounded-lg ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                        >
                          <div className="text-2xl font-bold text-[#7F56D9]">
                            {integration.syncStats.totalAccountsSynced || 0}
                          </div>
                          <div
                            className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Accounts
                          </div>
                        </div>
                        <div
                          className={`text-center p-4 rounded-lg ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                        >
                          <div className="text-2xl font-bold text-[#7F56D9]">
                            {integration.syncStats.totalOpportunitiesSynced ||
                              0}
                          </div>
                          <div
                            className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Opportunities
                          </div>
                        </div>
                        <div
                          className={`text-center p-4 rounded-lg ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                        >
                          <div className="text-2xl font-bold text-[#7F56D9]">
                            {integration.syncStats.totalCasesSynced || 0}
                          </div>
                          <div
                            className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            Cases
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div
                    className={`border rounded-lg p-6 transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
                  >
                    <h3
                      className={`text-lg font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      Quick Actions
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <button
                        onClick={() => setActiveTab("sync")}
                        className="px-4 py-3 bg-[#7F56D9] hover:bg-[#6B46C1] text-white rounded-lg transition-colors flex items-center gap-2 justify-center"
                      >
                        <Database className="w-4 h-4" />
                        Sync Data
                      </button>
                      <button
                        onClick={() => setActiveTab("test")}
                        className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2 justify-center"
                      >
                        <TestTube className="w-4 h-4" />
                        Test Tools
                      </button>
                      <button
                        onClick={handleSyncAll}
                        disabled={Object.values(syncingStates).some((v) => v)}
                        className="px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2 justify-center disabled:opacity-50"
                      >
                        <Upload className="w-4 h-4" />
                        Sync All
                      </button>
                      <button
                        onClick={() => setActiveTab("settings")}
                        className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 justify-center ${isDarkMode ? "bg-gray-600 hover:bg-gray-700 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"}`}
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div
                  className={`border rounded-lg p-12 text-center transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
                >
                  <Database className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                  <h3
                    className={`text-xl font-medium mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Salesforce Not Connected
                  </h3>
                  <p
                    className={`mb-6 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Connect your Salesforce account to start syncing data
                  </p>
                  <button
                    onClick={handleConnect}
                    className="px-6 py-2 bg-[#7F56D9] hover:bg-[#6B46C1] text-white rounded-lg transition-colors"
                  >
                    Connect Now
                  </button>
                </div>
              )}
            </>
          )}

          {/* Sync Tab */}
          {activeTab === "sync" && integration && (
            <>
              <div
                className={`border rounded-lg p-6 mb-6 transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3
                      className={`text-lg font-medium mb-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      Sync All Entities
                    </h3>
                    <p
                      className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Synchronize all Salesforce data at once
                    </p>
                  </div>
                  <button
                    onClick={handleSyncAll}
                    disabled={Object.values(syncingStates).some((v) => v)}
                    className={`px-6 py-3 bg-[#7F56D9] hover:bg-[#6B46C1] text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {Object.values(syncingStates).some((v) => v) ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Syncing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Sync All Entities
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {syncEntities.map((entity) => {
                  const Icon = entity.icon;
                  const isSyncing = syncingStates[entity.id];
                  return (
                    <div
                      key={entity.id}
                      className={`border rounded-lg p-5 transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700 hover:border-gray-600" : "bg-white border-gray-200 shadow-sm hover:border-gray-300"}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 ${colorClasses[entity.color]} rounded-lg flex items-center justify-center`}
                          >
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4
                              className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {entity.name}
                            </h4>
                            <p
                              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {entity.description}
                            </p>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleSync(entity.id, entity.syncMethod)}
                        disabled={isSyncing}
                        className={`w-full mt-3 px-4 py-2 ${colorClasses[entity.color]} text-white rounded-lg transition-colors flex items-center gap-2 justify-center disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {isSyncing ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Syncing...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Sync {entity.name}
                          </>
                        )}
                      </button>
                      {syncResults[entity.id] && (
                        <div
                          className={`mt-3 p-3 rounded-lg ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                        >
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }
                              >
                                Created:
                              </span>
                              <span className="text-green-500 ml-1 font-medium">
                                {syncResults[entity.id].created || 0}
                              </span>
                            </div>
                            <div>
                              <span
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }
                              >
                                Updated:
                              </span>
                              <span className="text-blue-500 ml-1 font-medium">
                                {syncResults[entity.id].updated || 0}
                              </span>
                            </div>
                            <div>
                              <span
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }
                              >
                                Skipped:
                              </span>
                              <span className="text-yellow-500 ml-1 font-medium">
                                {syncResults[entity.id].skipped || 0}
                              </span>
                            </div>
                            <div>
                              <span
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }
                              >
                                Failed:
                              </span>
                              <span className="text-red-500 ml-1 font-medium">
                                {syncResults[entity.id].failed || 0}
                              </span>
                            </div>
                          </div>
                          {syncResults[entity.id].sessionId && (
                            <div
                              className={`mt-2 text-xs truncate ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                            >
                              Session: {syncResults[entity.id].sessionId}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Testing Tab */}
          {activeTab === "test" && integration && (
            <>
              <div
                className={`border rounded-lg p-6 transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
              >
                <h3
                  className={`text-lg font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Testing & Debugging Tools
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <button
                    onClick={handleGetSampleLead}
                    disabled={isTesting}
                    className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 justify-center border disabled:opacity-50 ${isDarkMode ? "bg-[#0C0E12] hover:bg-[#22262F] text-white border-gray-700" : "bg-white hover:bg-gray-50 text-gray-700 border-gray-200 shadow-sm"}`}
                  >
                    <FileText className="w-4 h-4" />
                    Get Sample Lead
                  </button>
                  <button
                    onClick={handleAnalyzeLeads}
                    disabled={isTesting}
                    className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 justify-center border disabled:opacity-50 ${isDarkMode ? "bg-[#0C0E12] hover:bg-[#22262F] text-white border-gray-700" : "bg-white hover:bg-gray-50 text-gray-700 border-gray-200 shadow-sm"}`}
                  >
                    <Database className="w-4 h-4" />
                    Analyze Leads
                  </button>
                  <button
                    onClick={handleTestConnection}
                    disabled={isTesting}
                    className={`px-4 py-3 rounded-lg transition-colors flex items-center gap-2 justify-center border disabled:opacity-50 ${isDarkMode ? "bg-[#0C0E12] hover:bg-[#22262F] text-white border-gray-700" : "bg-white hover:bg-gray-50 text-gray-700 border-gray-200 shadow-sm"}`}
                  >
                    <TestTube className="w-4 h-4" />
                    Test Connection
                  </button>
                </div>
              </div>

              {sampleLead && (
                <div
                  className={`border rounded-lg p-6 transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
                >
                  <h3
                    className={`text-lg font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Sample Lead Data
                  </h3>
                  <div
                    className={`rounded-lg p-4 overflow-auto max-h-96 ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                  >
                    <pre
                      className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      {JSON.stringify(sampleLead, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {analysis && (
                <div
                  className={`border rounded-lg p-6 transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
                >
                  <h3
                    className={`text-lg font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Leads Analysis
                  </h3>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div
                      className={`rounded-lg p-4 ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                    >
                      <div className="text-2xl font-bold text-[#7F56D9]">
                        {analysis.analysis?.totalLeads || 0}
                      </div>
                      <div
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Total Leads
                      </div>
                    </div>
                    <div
                      className={`rounded-lg p-4 ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                    >
                      <div className="text-2xl font-bold text-yellow-500">
                        {analysis.analysis?.missingEmails || 0}
                      </div>
                      <div
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Missing Emails
                      </div>
                    </div>
                    <div
                      className={`rounded-lg p-4 ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                    >
                      <div className="text-2xl font-bold text-red-500">
                        {analysis.analysis?.missingCompanies || 0}
                      </div>
                      <div
                        className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Missing Companies
                      </div>
                    </div>
                  </div>
                  <div
                    className={`rounded-lg p-4 overflow-auto max-h-96 ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                  >
                    <pre
                      className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      {JSON.stringify(analysis, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {Object.keys(syncResults).length > 0 && (
                <div
                  className={`border rounded-lg p-6 transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
                >
                  <h3
                    className={`text-lg font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Recent Sync Results
                  </h3>
                  <div className="space-y-4">
                    {Object.entries(syncResults).map(([entityId, result]) => {
                      const entity = syncEntities.find(
                        (e) => e.id === entityId,
                      );
                      return (
                        <div
                          key={entityId}
                          className={`rounded-lg p-4 ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                        >
                          <h4
                            className={`font-medium mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {entity?.name || entityId}
                          </h4>
                          <div className="grid grid-cols-4 gap-4 mb-3">
                            <div className="text-center">
                              <div className="text-xl font-bold text-green-500">
                                {result.created || 0}
                              </div>
                              <div
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Created
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-xl font-bold text-blue-500">
                                {result.updated || 0}
                              </div>
                              <div
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Updated
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-xl font-bold text-yellow-500">
                                {result.skipped || 0}
                              </div>
                              <div
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Skipped
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-xl font-bold text-red-500">
                                {result.failed || 0}
                              </div>
                              <div
                                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              >
                                Failed
                              </div>
                            </div>
                          </div>
                          {result.sessionId && (
                            <div
                              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                            >
                              Session ID:{" "}
                              <span
                                className={`font-mono ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                              >
                                {result.sessionId}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Settings Tab */}
          {activeTab === "settings" && integration && (
            <div className="space-y-6">
              <div
                className={`border rounded-lg p-6 transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
              >
                <h3
                  className={`text-lg font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Integration Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Organization ID
                    </label>
                    <input
                      type="text"
                      value={integration.organizationId}
                      readOnly
                      className={`w-full rounded-lg px-4 py-2 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-gray-400" : "bg-gray-50 border border-gray-200 text-gray-500"}`}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Organization Name
                    </label>
                    <input
                      type="text"
                      value={integration.organizationName}
                      readOnly
                      className={`w-full rounded-lg px-4 py-2 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-gray-400" : "bg-gray-50 border border-gray-200 text-gray-500"}`}
                    />
                  </div>
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                    >
                      Instance URL
                    </label>
                    <input
                      type="text"
                      value={integration.instanceUrl || ""}
                      readOnly
                      className={`w-full rounded-lg px-4 py-2 ${isDarkMode ? "bg-[#0C0E12] border border-gray-700 text-gray-400" : "bg-gray-50 border border-gray-200 text-gray-500"}`}
                    />
                  </div>
                </div>
              </div>

              <div
                className={`border rounded-lg p-6 transition-colors ${isDarkMode ? "bg-[#1a1d23] border-gray-700" : "bg-white border-gray-200 shadow-sm"}`}
              >
                <h3
                  className={`text-lg font-medium mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Available Entities
                </h3>
                <p
                  className={`text-sm mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  The following Salesforce entities are available for
                  synchronization:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {syncEntities.map((entity) => {
                    const Icon = entity.icon;
                    return (
                      <div
                        key={entity.id}
                        className={`flex items-center gap-2 p-3 rounded-lg ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
                      >
                        <Icon className="w-4 h-4 text-gray-400" />
                        <span
                          className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {entity.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
